import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  MessageSquare,
  LogOut,
  Dumbbell,
} from 'lucide-react';
import { supabase } from '../../services/supabase';
import { noLeidosDeMensajes } from '../../lib/buzonDelClub';
import { useAuthStore } from '../../store/authStore';

const BASE_NAV = [
  { to: '/monitor/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/monitor/agenda', icon: Calendar, label: 'Mi Agenda' },
  { to: '/monitor/evaluaciones', icon: ClipboardList, label: 'Evaluaciones' },
  { to: '/monitor/comunicaciones', icon: MessageSquare, label: 'Comunicaciones' },
];

const GYM_NAV = { to: '/monitor/gym', icon: Dumbbell, label: 'Preparación Física' };

export default function MonitorSidebar() {
  const { reset, profile } = useAuthStore((s) => ({ reset: s.reset, profile: s.profile }));
  const location = useLocation();
  // `null` = no se ha podido contar. No es 0: con 0 el globo no se pinta, y un
  // globo que no está dice «no tienes mensajes». El monitor no vuelve a entrar,
  // que es exactamente cómo el BUG 13 estuvo dos semanas sin que nadie lo
  // notara.
  const [unreadCount, setUnreadCount] = useState<number | null>(0);
  /** El recuento, para quien lo necesite fuera del efecto que lo define. */
  const recontarRef = useRef<() => void>(() => {});
  const nav = profile?.is_physical_trainer
    ? [BASE_NAV[0], BASE_NAV[1], GYM_NAV, BASE_NAV[2], BASE_NAV[3]]
    : BASE_NAV;

  useEffect(() => {
    if (!profile) return;

    const fetchCount = async () => {
      setUnreadCount(await noLeidosDeMensajes(profile.id));
    };
    recontarRef.current = fetchCount;
    fetchCount();

    const channel = supabase
      .channel(`monitor-sidebar-msgs-${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${profile.id}`,
        },
        fetchCount
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${profile.id}`,
        },
        fetchCount
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // Dep deliberada `[profile?.id]`: solo el id determina la suscripción
    // (canal + filtro `receiver_id`). Listar `profile` entero re-ejecutaría el
    // efecto en cada update del store de auth (refresh de avatar, etc.) y
    // tumbaría el canal Realtime innecesariamente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // ⚠️ EL MISMO CERO A MANO QUE LA BARRA DEL ADMIN, Y SE ARREGLA IGUAL.
  //
  // Esto hacía `setUnreadCount(0)` al navegar a Comunicaciones: un cero sin
  // haber leído nada ni contado nada. El globo no decía «lo has leído», decía
  // «he entrado en la pantalla», y al siguiente refresco volvía el número.
  //
  // Apareció en el censo de ceros escritos a mano que se hizo al encontrar el
  // de `Sidebar`: era la misma línea, carácter por carácter. Se cuenta aquí
  // porque el de al lado lo encontré mutando, por casualidad, y uno así no se
  // busca por casualidad dos veces.
  //
  // Nota de alcance: este contador es `noLeidosDeMensajes` —lo dirigido a la
  // persona—, no el buzón del club, así que el predicado del resto de este PR
  // no le toca. Lo que se arregla es solo el cero de la ruta.
  useEffect(() => {
    if (location.pathname.includes('comunicaciones')) recontarRef.current();
  }, [location.pathname]);

  async function handleLogout() {
    await supabase.auth.signOut();
    reset();
  }

  return (
    <aside className="w-64 min-h-screen bg-bg-surface border-r border-bg-border flex flex-col fixed left-0 top-0 bottom-0 z-10">
      {/* Logo */}
      <div className="p-6 border-b border-bg-border">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="VERTEX Performance" className="h-12 w-auto" />
          <p className="text-gold-primary text-xs font-inter uppercase tracking-widest">
            Entrenador
          </p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {nav.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`monitor-sidebar-link-${to.split('/').pop()}`}
            className={({ isActive }) => (isActive ? 'sidebar-item-active' : 'sidebar-item')}
          >
            <Icon size={16} />
            <span className="flex-1">{label}</span>
            {to === '/monitor/comunicaciones' && unreadCount === null && (
              <span
                className="min-w-[20px] h-5 rounded-full bg-error/20 text-error border border-error/40 text-xs font-bold flex items-center justify-center px-1"
                title="No se han podido contar los mensajes sin leer"
                data-testid="monitor-sidebar-no-leidos-sin-contar"
              >
                ?
              </span>
            )}
            {to === '/monitor/comunicaciones' && (unreadCount ?? 0) > 0 && (
              <span
                data-testid="monitor-sidebar-no-leidos"
                className="min-w-[20px] h-5 rounded-full bg-[#A3E635] text-[#0A0A0A] text-xs font-bold flex items-center justify-center px-1"
              >
                {(unreadCount ?? 0) > 99 ? '99+' : unreadCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-bg-border">
        <button onClick={handleLogout} className="sidebar-item w-full">
          <LogOut size={16} />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );
}
